import { combineReducers, Store, Action, applyMiddleware, createStore, DeepPartial, compose } from "../lib/redux";
import { data } from "./data/reducers";
import { cache } from "./cache/reducers";
import { mobile } from "./mobile/reducers";
import { IDataStore, DataActionTypes } from "./data/types";
import { IMobileStore, MobileActionTypes } from "./mobile/types";
import { middlewareLogger, createDatabaseStorageMiddleware, setClockDriftMiddleware } from "./middleware";
import { UserActionTypes } from "./users/types";
import { users } from "./users/reducers";
import { systemHosLogs, userHosLogs, historicalDayTracker } from "./hosLogs/reducers";
import DutyStatusLog, { IAnnotation } from "../model/DutyStatusLog";
import { SystemHosLogActionTypes, UserHosLogActionTypes, HistoricalDayActionTypes, IHistoricalDayTracker } from "./hosLogs/types";
import UserHosRuleSet from "../model/UserHosRuleSet";
import { UserHosRuleSetActionTypes } from "./userHosRuleSet/types";
import { userHosRuleSets } from "./userHosRuleSet/reducers";
import Trip from "../model/Trip";
import { TripActionTypes } from "./trips/types";
import { trips, tripsTodayCount } from "./trips/reducers";
import { TripAnnotationActionTypes } from "./tripAnnotations/types";
import TripAnnotation from "../model/TripAnnotation";
import { tripAnnotations } from "./tripAnnotations/reducers";
import GroupLinkedDevice from "../model/GroupLinkedDevice";
import { GroupLinkedDeviceActionTypes } from "./groupLinkedDevice/types";
import { groupLinkedDevices } from "./groupLinkedDevice/reducers";
import { DvirLogActionTypes } from "./dvirLogs/types";
import { IDVIRLog } from "../model/DVIRLog";
import { dvirLogs, dvirRepairNeeded } from "./dvirLogs/reducers";
import { FeedVersionActionTypes } from "./feedVersions/types";
import { feedVersions } from "./feedVersions/reducers";
import { TimeZoneInfoActionTypes } from "./timeZoneInfo/types";
import { ITimeZoneInfo } from "../utils/date";
import { timeZoneInfo } from "./timeZoneInfo/reducers";
import { DailyOdometerActionTypes } from "./dailyOdometer/types";
import { IDailyOdometer } from "../model/DutyStatusLogs";
import { dailyOdometer } from "./dailyOdometer/reducers";
import { IDefect } from "../model/Defect";
import { DeviceDefectListActionTypes, TrailerDefectListActionTypes } from "./defectLists/types";
import { deviceDefectLists, trailerDefectLists } from "./defectLists/reducers";
import { translations } from "./translation/reducers";
import { IMessage, IRoute } from "../model/Message";
import { messages, messageRevision } from "./messages/reducers";
import { MessageActionTypes } from "./messages/types";
import { IDriverChange } from "../model/DriverChangeLog";
import { DriverChangesActionTypes } from "./driverChanges/types";
import { driverChanges } from "./driverChanges/reducers";
import { ErrorActionTypes, IError } from "./error/types";
import { error } from "./error/reducers";
import { HOSAnnotationActionTypes } from "./hosAnnotations/types";
import { hosAnnotations } from "./hosAnnotations/reducers";
import { RouteActionTypes } from "./routes/types";
import { routes } from "./routes/reducers";
import { ITrailerAttachment } from "../model/TrailerAttachment";
import { trailerAttachments } from "./trailerAttachments/reducers";
import { deviceLinks } from "./deviceLinks/reducers";
import { DeviceLinkActionTypes } from "./deviceLinks/types";
import { IDeviceLink } from "../model/DeviceLink";
import { TrailerAttachmentActionTypes } from "./trailerAttachments/types";
import { ShipmentActionTypes } from "./shipment/types";
import { IShipment } from "../model/Shipment";
import { shipments } from "./shipment/reducers";
import { ISuspiciousLogsSent, SuspiciousLogsSentActionTypes } from "./suspiciousLogsSent/types";
import { suspiciousLogsSent } from "./suspiciousLogsSent/reducers";
import { ActiveUserActionTypes } from "./active/types";
import { activeUsers } from "./active/reducers";
import { driveAPICredentials } from "./driveAPICredentials/reducers";
import { DriveAPICredentialsActionTypes } from "./driveAPICredentials/types";
import { CacheActionTypes, ICacheStore } from "./cache/types";
import { ICredentialsWithServer } from "../utils/credentialsStore";
import { IDeviceMovement, DeviceMovementActionTypes } from "./deviceMovement/types";
import { deviceMovement } from "./deviceMovement/reducers";
import { openCabUsers } from "./openCabUsers/reducers";
import { OpenCabUsersActionTypes } from "./openCabUsers/types";
import { IOpenCabWorkflow, OpenCabWorkflowActionTypes } from "./openCabWorkflow/types";
import { openCabWorkflow } from "./openCabWorkflow/reducers";
import { IamCredentials } from "./iamCredentials/actions";
import { IamCredentialsActionTypes } from "./iamCredentials/types";
import { iamCredentials } from "./iamCredentials/reducers";
import { ISystemSettings, SystemSettingsActionTypes } from "./systemSettings/types";
import { systemSettings } from "./systemSettings/reducers";
import { openCabQueue } from "./openCabQueue/reducers";
import { OpenCabQueue, OpenCabQueueActionTypes } from "./openCabQueue/types";
import { vehicleHistory } from "./vehicleHistory/reducers";
import { IVehicleHistoryStore, VehicleHistoryActionTypes } from "./vehicleHistory/types";
import { IModuleReducer, ModuleActions, moduleReducers } from "../modules/reducers";
import { ScheduleStatusActionTypes } from "./scheduleStatus/types";
import { scheduleStatus } from "./scheduleStatus/reducers";
import { ITranslations } from "../model/Translation";
import { TranslationActionTypes } from "./translation/types";
import { DriverSeatUserActionTypes } from "./driverSeat/types";
import { driverSeatUser } from "./driverSeat/reducers";
import { HosRuleResultsActionTypes } from "./hosRuleResults/types";
import { hosRuleResults, IHosRuleResult } from "./hosRuleResults/reducers";
import { compareStorage, isNativeStorageAvailable } from "../utils/native/secureStorageApi";
import { preload } from "./preload";

export interface IGlobalReducer {
    activeUsers: DeepReadonlyArray<string>;
    cache: ICacheStore;
    data: IDataStore;
    driveAPICredentials: IIdDictionary<ICredentialsWithServer>;
    hosAnnotations: IIdDictionary<IIdDictionary<IAnnotation>>;
    mobile: IMobileStore;
    users: IIdDictionary<IUser>;
    userHosLogs: IIdDictionary<IIdDictionary<DutyStatusLog>>;
    systemHosLogs: IIdDictionary<IIdDictionary<DutyStatusLog>>;
    historicalDayTracker: IHistoricalDayTracker;
    userHosRuleSets: IIdDictionary<IIdDictionary<UserHosRuleSet>>;
    dvirLogs: IIdDictionary<IIdDictionary<IDVIRLog>>;
    trips: IIdDictionary<IIdDictionary<Trip>>;
    tripAnnotations: IIdDictionary<IIdDictionary<TripAnnotation>>;
    groupLinkedDevices: IIdDictionary<IIdDictionary<GroupLinkedDevice>>;
    feedVersions: IIdDictionary<IIdDictionary<string | IIdDictionary<string>>>;
    timeZoneInfo: IIdDictionary<ITimeZoneInfo>;
    dailyOdometer: IIdDictionary<IDailyOdometer>;
    deviceDefectLists: IIdDictionary<IIdDictionary<IDefect[]>>;
    trailerDefectLists: IIdDictionary<IIdDictionary<IDefect[]>>;
    messages: IIdDictionary<IIdDictionary<IMessage>>;
    messageRevision: number;
    driverChanges: IIdDictionary<IDriverChange[]>;
    routes: IIdDictionary<IIdDictionary<IRoute>>;
    error: IError;
    trailerAttachments: IIdDictionary<IIdDictionary<ITrailerAttachment>>;
    deviceLinks: IIdDictionary<IIdDictionary<IDeviceLink>>;
    shipments: IIdDictionary<IIdDictionary<IShipment>>;
    suspiciousLogsSent: ISuspiciousLogsSent;
    deviceMovement: IDeviceMovement;
    openCabUsers: IIdDictionary<string>;
    openCabWorkflow: IOpenCabWorkflow;
    openCabQueue: OpenCabQueue[];
    iamCredentials: IIdDictionary<IamCredentials>;
    systemSettings: ISystemSettings;
    vehicleHistory: IVehicleHistoryStore;
    scheduleStatus: IIdDictionary<boolean>;
    tripsTodayCount: IIdDictionary<number>;
    dvirRepairNeeded: IIdDictionary<IIdDictionary<boolean>>;
    translations: ITranslations;
    driverSeatUser: DeepReadonly<string>;
    hosRuleResults: IIdDictionary<IHosRuleResult>;
}

export type GlobalReducer = DeepReadonly<IGlobalReducer & IModuleReducer>;

// eslint-disable-next-line max-len
export type StoreActions = ActiveUserActionTypes | CacheActionTypes | MobileActionTypes | DataActionTypes | DriveAPICredentialsActionTypes | UserActionTypes | UserHosLogActionTypes |
    SystemHosLogActionTypes | HistoricalDayActionTypes | UserHosRuleSetActionTypes | TripActionTypes | TripAnnotationActionTypes | GroupLinkedDeviceActionTypes |
    ShipmentActionTypes | DvirLogActionTypes | FeedVersionActionTypes | TimeZoneInfoActionTypes | DailyOdometerActionTypes | MessageActionTypes | ErrorActionTypes |
    DriverChangesActionTypes | DeviceDefectListActionTypes | TrailerDefectListActionTypes | HOSAnnotationActionTypes | RouteActionTypes | TrailerAttachmentActionTypes |
    SuspiciousLogsSentActionTypes | DeviceMovementActionTypes | OpenCabUsersActionTypes | OpenCabWorkflowActionTypes | OpenCabQueueActionTypes | DeviceLinkActionTypes |
    IamCredentialsActionTypes | SystemSettingsActionTypes | VehicleHistoryActionTypes | ScheduleStatusActionTypes | TranslationActionTypes | DriverSeatUserActionTypes |
    HosRuleResultsActionTypes;

export type DriveStore = Store<GlobalReducer, Action<StoreActions | ModuleActions>>;

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose;
    }
}

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

export async function buildStore(preLoadStateOverride?: DeepReadonly<DeepPartial<GlobalReducer>>): Promise<DriveStore> {
    const storeEnhancer = composeEnhancers(
        applyMiddleware<DriveStore>(
            middlewareLogger,
            createDatabaseStorageMiddleware(),
            setClockDriftMiddleware
        )
    );
    const combinedReducers = combineReducers({
        cache,
        activeUsers,
        mobile,
        data,
        driveAPICredentials,
        users,
        userHosLogs,
        systemHosLogs,
        userHosRuleSets,
        dvirLogs,
        trips,
        tripAnnotations,
        groupLinkedDevices,
        feedVersions,
        timeZoneInfo,
        dailyOdometer,
        messages,
        messageRevision,
        driverChanges,
        deviceDefectLists,
        trailerDefectLists,
        hosAnnotations,
        routes,
        error,
        trailerAttachments,
        deviceLinks,
        shipments,
        suspiciousLogsSent,
        deviceMovement,
        openCabUsers,
        openCabWorkflow,
        openCabQueue,
        iamCredentials,
        systemSettings,
        vehicleHistory,
        scheduleStatus,
        tripsTodayCount,
        dvirRepairNeeded,
        translations,
        driverSeatUser,
        hosRuleResults,
        historicalDayTracker,
        ...moduleReducers
    });

    const preloadState = await preload();
    if (isNativeStorageAvailable()) {
        void compareStorage();
    }
    return createStore(
        combinedReducers,
        preLoadStateOverride || preloadState as any,
        storeEnhancer
    );
}